import { bool, func, object } from 'prop-types'
import { Component } from 'react'
import { getTrackableComponent } from '@opallabs/quanta'
import RawFilterBar from './unmanaged'
import { addFilter, addLabelSetFilter, addStatusFilter, flatten } from './utils'
import { parseDateRangeEvent, parseFilterEvent } from './utils/analytics'

class FilterBar extends Component {
  static propTypes = {
    ...RawFilterBar.propTypes,
    filtersDisabled: bool,
    focused: bool,
    onChange: func,
    storeValues: bool,
    toggleFilters: func,
    trackers: object,
  }

  static defaultProps = {
    theme: 'light',
    trackers: {},
  }

  constructor(props) {
    super(props)

    const now = new Date().toISOString()

    const value = props.value || []
    const disabledValues = props.disabledValues || []

    this.state = {
      value,
      disabledValues,
      filterValues: {
        duration_starts_at:
          (props.filterValues && props.filterValues.duration_starts_at) || now,
        duration_ends_at:
          (props.filterValues && props.filterValues.duration_ends_at) || now,
      },
      lastPath: [],
      disabled: value.every((v) => disabledValues.indexOf(v) !== -1),
    }

    if (props.loadFilters) {
      props.loadFilters({
        ...props,
        rawValue: this.value,
        selectedFilters: this.buildSelectedFilters(),
      })
    }

    this.pathChange = this.pathChange.bind(this)
    this.filterSelect = this.filterSelect.bind(this)
    this.filterRemove = this.filterRemove.bind(this)
    this.filterValueChange = this.filterValueChange.bind(this)
    this.search = this.search.bind(this)
    this.blur = this.blur.bind(this)
    this.focus = this.focus.bind(this)
    this.disableAll = this.disableAll.bind(this)
    this.disableFilter = this.disableFilter.bind(this)
    this.clear = this.clear.bind(this)
    this.valueFor = this.valueFor.bind(this)
    this.doTracking = this.doTracking.bind(this)
    this.filterTitleById = this.filterTitleById.bind(this)

    if (props.onInit) props.onInit(this.value, this.metaValue)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.updatingFilters) {
      this.updatingFilters = true
      if (nextProps.loadFilters) {
        nextProps.loadFilters({
          ...nextProps,
          rawValue: this.value,
          selectedFilters: this.buildSelectedFilters(),
        })
      }
      setTimeout(() => {
        this.updatingFilters = false
      })
    }
  }

  get track() {
    return this.props.trackers.track
  }

  doTracking(label, val) {
    if (this.track) {
      this.track(label, val)
    }
  }

  valueFor(filter) {
    if (filter.type === 'date') {
      return this.state.filterValues[filter.id] || new Date().toISOString()
    }
    return this.state.filterValues[filter.id]
  }

  filterTitleById(filterId) {
    const flatFilterTree = flatten(this.props.filterTree)
    const { title } = flatFilterTree.find((f) => f.id === filterId) || {}
    return title
  }

  valuePayload(filter) {
    if (filter.payload) {
      if (filter.payload.object) {
        if (typeof filter.payload.object === 'string') {
          return filter.payload
        }
        return {
          ...filter.payload,
          object: {
            ...filter.payload.object,
            value: this.valueFor(filter),
          },
        }
      }
      return {
        ...filter.payload,
        object: {
          value: this.valueFor(filter),
        },
      }
    }
    return {}
  }

  get value() {
    const { value, disabledValues } = this.state
    const { filterTree } = this.props
    const flatTree = filterTree.flattened || flatten(filterTree)
    return value
      .map((filterId) => flatTree.find((node) => node.id === filterId))
      .filter((filter) => filter)
      .map((filter) => ({
        ...filter,
        payload: this.valuePayload(filter),
        disabled: disabledValues.indexOf(filter.id) > -1,
      }))
  }

  get metaValue() {
    const { value, disabledValues, filterValues, disabled } = this.state
    return {
      disabled: Boolean(disabled),
      value: {
        selected: value || [],
        disabled: disabledValues || [],
        filterValues: filterValues || {},
      },
    }
  }

  trackFilterSelectEvent(filterId) {
    const { name, data } = parseFilterEvent(
      [...(this.state.currentPath || []), filterId],
      this.props.filterTree
    )
    if (name && data) {
      this.doTracking(name, data)
    }
  }

  trackDateRangeEvent() {
    const { name, data } = parseDateRangeEvent(this.state.filterValues)
    if (name && data) {
      this.doTracking(name, data)
    }
  }

  buildSelectedFilters() {
    const selectedFilters = {}
    this.value.forEach((filter) => {
      switch (filter.payload.type_key) {
        case 'labels':
          selectedFilters.labels = addLabelSetFilter(
            filter,
            selectedFilters.labels,
            this.state.disabled
          )
          break
        case 'services_and_accounts':
          if (filter.payload.object.service_id) {
            selectedFilters.account = addFilter(
              filter,
              selectedFilters.account,
              this.state.disabled
            )
          }
          if (!filter.payload.object.service_id) {
            selectedFilters.service = addFilter(
              filter,
              selectedFilters.service,
              this.state.disabled
            )
          }
          break
        case 'statuses':
          selectedFilters.status = addStatusFilter(
            filter,
            selectedFilters.status,
            this.state.disabled
          )
          break
        default:
          break
      }
    })

    return selectedFilters
  }

  changed() {
    if (this.props.onChange) {
      const activeFilters = this.value
      if (this.props.loadFilters && this.props.storeValues) {
        this.props
          .onChange(activeFilters, this.metaValue)
          .then(() =>
            this.props.loadFilters({
              ...this.props,
              value: this.value.map((value) => value.id),
              rawValue: this.value,
              selectedFilters: this.buildSelectedFilters(),
            })
          )
          .catch((error) => {
            throw error
          })
        return
      }
      this.props.onChange(activeFilters, this.metaValue)
    }
  }

  pathChange(newPath) {
    this.setState((prevState) => ({
      lastPath: prevState.currentPath || [],
      currentPath: newPath,
    }))
    if (this.props.onPathChange) {
      this.props.onPathChange(newPath)
    }
  }

  filterSelect(filter) {
    this.trackFilterSelectEvent(filter.id)
    this.setState((lastState) => {
      const valuesToAdd = Array.isArray(filter)
        ? filter.map(({ id }) => id)
        : [filter.id]
      const newValue = [...lastState.value, ...valuesToAdd]
      if (this.props.updateFilterProps) {
        this.props.updateFilterProps(newValue)
      }

      if (this.props.onFilterSelect) {
        valuesToAdd.forEach((filterId) =>
          this.props.onFilterSelect({ id: filterId, disabled: false })
        )
      }

      return {
        value: newValue,
        disabled: newValue.every(
          (v) => lastState.disabledValues.indexOf(v) !== -1
        ),
      }
    }, this.changed)
  }

  filterRemove(filter) {
    const filterTitle = this.filterTitleById(filter)

    this.doTracking('Filter: Clear One', { filter: filterTitle })
    this.setState((lastState) => {
      const valuesToRemove = Array.isArray(filter) ? filter : [filter]
      const value = lastState.value.filter(
        (v) => valuesToRemove.find((f) => v === f) === undefined
      )
      const disabledValues = lastState.disabledValues.filter(
        (v) => valuesToRemove.find((f) => v === f) === undefined
      )
      if (this.props.onFilterRemove) {
        valuesToRemove.forEach(() => this.props.onFilterRemove(filter))
      }
      return {
        value,
        disabledValues,
      }
    }, this.changed)
  }

  filterValueChange(id, value) {
    this.setState(
      (lastState) => ({
        filterValues: {
          ...lastState.filterValues,
          [id]: value,
        },
      }),
      () => {
        this.trackDateRangeEvent()
        this.changed()
      }
    )
    if (this.props.onFilterValueChange) {
      this.props.onFilterValueChange(id, value)
    }
  }

  search(term) {
    this.setState({
      currentPath: undefined,
      searchTerm: term,
    })
    if (this.props.onSearch) {
      this.props.onSearch(term)
    }
  }

  focus() {
    this.setState(
      {
        focused: true,
      },
      () => this.props.onFocus && this.props.onFocus()
    )
  }

  blur() {
    this.setState({
      focused: false,
    })
  }

  disableAll() {
    this.doTracking('Filter: Toggle All', {
      toggleFilters: this.state.disabled || false,
    })
    this.setState(
      (lastState) => ({
        disabled: !lastState.disabled,
        disabledValues: lastState.disabled ? [] : [...lastState.value],
      }),
      this.changed
    )
    if (this.props.toggleFilters) this.props.toggleFilters()
  }

  disableFilter(filterId, value) {
    const filterTitle = this.filterTitleById(filterId)

    this.doTracking('Filter: Toggle One', {
      enabled: !value,
      filter: filterTitle,
    })
    const { disabledValues } = this.state
    if (!value) {
      this.setState(
        (lastState) => ({
          disabled: false,
          disabledValues: lastState.disabledValues.filter(
            (v) => v !== filterId
          ),
        }),
        this.changed
      )
    } else {
      const newDisabled = [...disabledValues, filterId]
      this.setState(
        {
          disabled: this.state.value.every(
            (v) => newDisabled.indexOf(v) !== -1
          ),
          disabledValues: newDisabled,
        },
        this.changed
      )
    }
  }

  clear() {
    this.doTracking('Filter: Clear All', { removeFilters: true })
    if (this.props.updateFilterProps) this.props.updateFilterProps([])
    this.setState(
      {
        value: [],
        disabledValues: [],
      },
      this.changed
    )
  }

  render() {
    return (
      <RawFilterBar
        {...this.props}
        {...this.state}
        focused={this.props.focused || this.state.focused}
        onBlur={this.blur}
        onFocus={this.focus}
        onSearch={this.search}
        searchTerm={this.state.searchTerm}
        disabled={this.state.disabled}
        disabledValues={this.state.disabledValues}
        onDisableAll={this.disableAll}
        onDisableFilter={this.disableFilter}
        onClearFilters={this.clear}
        onPathChange={this.pathChange}
        onFilterSelect={this.filterSelect}
        onFilterRemove={this.filterRemove}
        onFilterValueChange={this.filterValueChange}
        track={this.track}
      />
    )
  }
}
FilterBar.displayName = 'Kuiper: FilterBar'

const TrackedFilterBar = getTrackableComponent(FilterBar)
export { TrackedFilterBar, RawFilterBar }
export default FilterBar
